import { createColumnHelper } from '@tanstack/react-table';
import { Link, Typography } from '@optimus-web/core';
import { LocalizedText, lt } from '@mews/framework';
import { ConvertedCurrencyValue, Dash } from '@mews-commander/ui';
import { TextKey } from '@mews-commander/enums';
import {
    AccountingConfigurationOptions,
    CashPaymentTableRecordData,
    PaymentTableRecord,
} from '@mews-commander/data-access';
import { UnicodeEntity } from '@mews/enums';
import { PaymentTablesColumns } from '../types';
import { PaymentTypeCell } from './PaymentTypeCell';
import { PaymentsActionsMenu } from './PaymentsActionsMenu';
import { isCashPayment } from './utils';
import { CreatedCell } from './CreatedCell';
import { PaymentsTableBillCell } from './PaymentsTableBillCell';
import { PaymentsTableStatusCell } from './PaymentsTableStatusCell';

interface Props {
    ownerId: string;
    openCashierDetailModal: (id: string) => void;
    openReservationDetailModal: (id: string) => void;
    refetchTableData: () => void;
    isPaymentAttemptsTable: boolean;
    displayReservationLink: boolean;
    receivableTrackingEnabled: AccountingConfigurationOptions['receivableTrackingEnabled'];
    isCompany?: boolean;
}

export interface PaymentTableRecordExtended extends PaymentTableRecord {
    isTotalAmount?: boolean;
}

const columnHelper = createColumnHelper<PaymentTableRecordExtended>();

export const getPaymentsColumns = ({
    ownerId,
    openCashierDetailModal,
    isPaymentAttemptsTable,
    displayReservationLink,
    openReservationDetailModal,
    isCompany = false,
    ...props
}: Props) => [
    columnHelper.accessor('id', {
        header: () => <LocalizedText textKey={TextKey.Type} />,
        cell: ({ row: { original: payment } }) =>
            payment.isTotalAmount ? (
                <Typography textStyle="bodyMediumStrong" as="div">
                    {lt('Total')}
                </Typography>
            ) : (
                <PaymentTypeCell payment={payment} isPaymentAttemptsTable={isPaymentAttemptsTable} />
            ),
        id: PaymentTablesColumns.Type,
    }),
    columnHelper.accessor('identifier', {
        header: () => <LocalizedText textKey={TextKey.Identifier} />,
        cell: ({
            row: {
                original: { identifier, isTotalAmount },
            },
        }) => !isTotalAmount && identifier,
        id: PaymentTablesColumns.Identifier,
    }),
    columnHelper.accessor('createdUtc', {
        header: () => <LocalizedText textKey={TextKey.Created} />,
        cell: ({
            row: {
                original: { createdUtc, creatorProfileName, isTotalAmount },
            },
        }) => !isTotalAmount && <CreatedCell createdUtc={createdUtc} creatorProfileName={creatorProfileName} />,
        id: PaymentTablesColumns.Created,
    }),
    columnHelper.accessor('data', {
        header: () => <LocalizedText textKey={TextKey.Cashier} />,
        cell: ({
            row: {
                original: { data, isTotalAmount },
            },
        }) => {
            if (isTotalAmount) {
                return null;
            }
            const { discriminator, value } = data;
            const { cashierId, cashierName }: CashPaymentTableRecordData = value || {};

            if (!isCashPayment(discriminator) || !cashierId || !cashierName) {
                return <Dash />;
            }
            return (
                <Link inherit id={cashierId} onClick={() => openCashierDetailModal(cashierId)}>
                    {cashierName}
                </Link>
            );
        },
        id: PaymentTablesColumns.Cashier,
    }),
    columnHelper.accessor('billId', {
        header: () => <LocalizedText textKey={TextKey.Bill} />,
        cell: ({ row: { original: payment } }) => {
            if (payment.isTotalAmount) {
                return null;
            }

            return <PaymentsTableBillCell accountId={ownerId} payment={payment} />;
        },
        id: PaymentTablesColumns.Bill,
    }),
    ...(displayReservationLink
        ? [
              columnHelper.accessor('serviceOrderId', {
                  header: () => <LocalizedText textKey={TextKey.Reservation} />,
                  cell: ({
                      row: {
                          original: { serviceOrderId, serviceOrderNumber, isTotalAmount },
                      },
                  }) => {
                      if (isTotalAmount) {
                          return null;
                      }

                      return serviceOrderId ? (
                          <Link inherit id={serviceOrderId} onClick={() => openReservationDetailModal(serviceOrderId)}>
                              {serviceOrderNumber}
                          </Link>
                      ) : (
                          <>{UnicodeEntity.Dash}</>
                      );
                  },
                  id: PaymentTablesColumns.Reservation,
              }),
          ]
        : []),
    columnHelper.accessor('notes', {
        header: () => <LocalizedText textKey={TextKey.Notes} />,
        cell: ({
            row: {
                original: { notes, isTotalAmount },
            },
        }) => !isTotalAmount && notes,
        id: PaymentTablesColumns.Notes,
    }),
    columnHelper.accessor('state', {
        header: () => <LocalizedText textKey={TextKey.Status} />,
        cell: ({
            row: {
                original: { transactionDetails, state, isTotalAmount },
            },
        }) => !isTotalAmount && <PaymentsTableStatusCell transactionDetails={transactionDetails} state={state} />,
        id: PaymentTablesColumns.Status,
    }),
    columnHelper.accessor('amount', {
        header: () => <LocalizedText textKey={TextKey.Value} />,
        cell: ({
            row: {
                original: { amount },
            },
        }) => <ConvertedCurrencyValue amount={amount} />,
        id: PaymentTablesColumns.Amount,
    }),
    columnHelper.accessor('actions', {
        header: () => <LocalizedText textKey={TextKey.ActionPlural} />,
        meta: {
            align: 'end',
        },
        cell: ({
            row: {
                original: { id, actions, isTotalAmount, serviceOrderId, accountingState, data },
            },
        }) =>
            !isTotalAmount &&
            actions && (
                <PaymentsActionsMenu
                    paymentActions={actions}
                    serviceOrderId={serviceOrderId}
                    paymentId={id}
                    ownerId={ownerId}
                    paymentAccountingState={accountingState}
                    paymentData={data}
                    isCompany={isCompany}
                    {...props}
                />
            ),
        id: PaymentTablesColumns.Actions,
    }),
];
